import React from "react"
import {
  FormControl,
  ControlLabel,
  Modal,
  Button,
  FormGroup,
  Row,
  Col,
  ButtonToolbar,
  ToggleButtonGroup,
  ToggleButton,
} from "react-bootstrap"
import moment from "moment"
import FieldGroup from "../../fragments/FieldGroup"
import FontAwesome from "react-fontawesome"
import { customerFieldsObject, lowerCamelCase, trimSpace, validateTextState } from "../../../lib/utils"
import ProcessesStatus from "../../fragments/ProcessesStatus"

const customerFieldType = Object.values(customerFieldsObject).sort()

export default class AddCustomerFieldModal extends React.Component {

  state = {
    label: '',
    placeHolder: '',
    type: '',
    min: 0,
    minHtml: '',
    max: 0,
    maxHtml: '',
    options: [],
    regex: '',
    mandatory: false,
    unique: false,
    error: null,
    typeValidationState: null,
    labelValidationState: null,
    placeHolderValidationState: null,
    regexValidationState: null,
    minValidationState: null,
    maxValidationState: null,
    optionsValidationState: [],
  }

  clearInputs = () => {
    this.setState({
      label: '',
      placeHolder: '',
      type: '',
      min: 0,
      minHtml: '',
      max: 0,
      maxHtml: '',
      options: [],
      regex: '',
      mandatory: false,
      unique: false,
      error: null,
      typeValidationState: null,
      labelValidationState: null,
      placeHolderValidationState: null,
      regexValidationState: null,
      minValidationState: null,
      maxValidationState: null,
      optionsValidationState: [],
    })
  }

  validateSelectState = value => value ? 'success' : 'error'

  validateMinMaxValues = (min, max, type) => {

    if(type === customerFieldsObject.NUMBER) {
      min = Number(min)
      max = Number(max)
    }
  
    if( !min || !max) return null
    if(this.isDateType(type)) {
      return moment(min).unix() <= moment(max).unix() ? null : 'error'
    }
    else {
      return min <= max ? null : 'error'
    }
  }

  areOptionsValid = options => options.every((item, index) => 
    options.indexOf(item) === index && validateTextState(item, false) === 'success')

  getOptionValidationStates = options => options.map((value, index) => 
    options.indexOf(value) !== index ? 'error' : validateTextState(value, false))

  isOptionType = type => type === customerFieldsObject.SELECT || type === customerFieldsObject.RADIO

  isDateType = type => type === customerFieldsObject.DATE || type === customerFieldsObject.DATETIME

  getIntegerValue = (value, type) => value && this.isDateType(type) 
    ? moment(value).unix() * 1000 : Number(value)

  typeChanged = type => {

    if(this.isOptionType(type)) {
      if(!this.state.options.length) {
        this.addNewOption()
      }
    }
    else {
      this.setState({ options: [], optionsValidationState: [], })
    }
  
    this.setState({
      type,
      min: 0,
      max: 0,
      minHtml: '',
      maxHtml: '',
      regex: '',
      typeValidationState: this.validateSelectState(type),
      regexValidationState: null,
      minValidationState: null,
      maxValidationState: null,
      error: null,
    })
  }

  labelChanged = label => {
    this.setState({
      label,
      labelValidationState: validateTextState(label, false),
      error: null,
    })
  }

  placeHolderChanged = placeHolder =>
    this.setState({
      placeHolder,
      placeHolderValidationState: validateTextState(placeHolder, false),
      error: null,
    })

  mandatoryChanged = value => this.setState({ mandatory: value === 'Yes' ? true : false })


  uniqueChanged = value => this.setState({ unique: value === 'Yes' ? true : false })

  regexChanged = regex =>
    this.setState({
      regex,
      regexValidationState: validateTextState(regex, true)
    })

  minChanged = (minValue, type) => {
    const minMaxValid = this.validateMinMaxValues(minValue, this.state.max, type)
    this.setState({
      min: this.getIntegerValue(minValue, type),
      minHtml: minValue,
      minValidationState: minMaxValid,
      maxValidationState: minMaxValid,
      error: null,
    })
  }

  maxChanged = (maxValue, type) => {
    const minMaxValid = this.validateMinMaxValues(this.state.min, maxValue, type)
    this.setState({
      max: this.getIntegerValue(maxValue, type),
      maxHtml: maxValue,
      minValidationState: minMaxValid,
      maxValidationState: minMaxValid,
      error: null,
    })
  }

  optionChanged = (value, index) => {

    const { options } = this.state
    options[index] = value
    this.setState({
      options,
      optionsValidationState: this.getOptionValidationStates(options),
      error: null,
    })
  }

  renderAdditionalFields = () => {
    const type = this.state.type
    switch(type) {
      case customerFieldsObject.TEXT:
      case customerFieldsObject.EMAIL:
        return this.renderRegexField()

      case customerFieldsObject.DATE:
      case customerFieldsObject.DATETIME:
      case customerFieldsObject.NUMBER:
        return this.renderMinMaxFields(type)

      case customerFieldsObject.RADIO:
      case customerFieldsObject.SELECT:
        return this.renderOptionFields()

      default: return null
    }
  }

  valid = ({ type, label, placeHolder, min, max, options }) => {

    const minMaxValid = (this.isDateType(type) || type === customerFieldsObject.NUMBER) ? 
      this.validateMinMaxValues(min, max, type) === null : true

    return (!this.isOptionType(type) || this.areOptionsValid(options))
      && this.validateSelectState(type) === 'success'
      && validateTextState(label, false) === 'success'
      && validateTextState(placeHolder, false) === 'success'
      && minMaxValid
  }

  createData = ({ label, placeHolder, type, mandatory, unique, min, max, options, regex }) => {

    const data = {
      name: lowerCamelCase(label),
      label: trimSpace(label),
      placeHolder: trimSpace(placeHolder),
      type,
      mandatory,
      unique
    }

    switch(type) {
      case customerFieldsObject.TEXT:
      case customerFieldsObject.EMAIL:
        data['regex'] = trimSpace(regex)
        break

      case customerFieldsObject.DATE:
      case customerFieldsObject.DATETIME:
      case customerFieldsObject.NUMBER:
        data['min'] = min
        data['max'] = max
        break

      case customerFieldsObject.RADIO:
      case customerFieldsObject.SELECT:
        const opts = {}
        options.forEach(item => opts[lowerCamelCase(item)] = trimSpace(item))
        data['options'] = opts
        break

      default: break
    }

    return data
  }

  saveField = () => {

    this.setState({ error: null })
    const data = {
      label: this.state.label,
      placeHolder: this.state.placeHolder,
      type: this.state.type,
      mandatory: this.state.mandatory,
      unique: this.state.unique,
      min: this.state.min,
      max: this.state.max,
      options: this.state.options,
      regex: this.state.regex,
    }

    try {
      if(!this.valid(data)) throw new Error('Some fields are incorrect.')
      const filledData = this.createData(data)
      if(this.props.infoExists(filledData.name)) throw new Error(`Duplicate field: ${ filledData.label }`)
      this.props.addInfo(filledData)

      setTimeout(this.closeModal, 200)
    }
    catch(e) {
      this.setState({
        error: e.message,
        typeValidationState: this.validateSelectState(data.type),
        labelValidationState: validateTextState(data.label, false),
        placeHolderValidationState: validateTextState(data.placeHolder, false),
        optionsValidationState: this.getOptionValidationStates(data.options)
      })
    }
  }

  addNewOption = () => {
    const { options, optionsValidationState } = this.state
    options.push('')
    optionsValidationState.push(null)
    this.setState({ options, optionsValidationState })
  }

  removeOption = index => {
    const { options } = this.state
    options.splice(index, 1)
    this.setState({
      options,
      optionsValidationState: this.getOptionValidationStates(options),
      error: null,
    })
  }

  closeModal = () => {
    this.props.close()
    this.clearInputs()
  }

  renderRegexField = () => {

    return <Col xs={6}>
      <FieldGroup
        id="regex"
        type="text"
        label="Regex (Optional)"
        placeholder="Ex: ^(?=.*[a-z])$"
        validationState={ this.state.regexValidationState }
        value={ this.state.regex }
        onChange={e => this.regexChanged(e.target.value)}
        maxLength={512}
      />
    </Col>
  }

  renderMinMaxFields = type => {

    const htmlType = type === customerFieldsObject.DATE 
      ? 'date' : type === customerFieldsObject.DATETIME 
      ? 'datetime-local' : 'number'

    return <div>
      <Col xs={6}>
        <FieldGroup
          id="min"
          type={ htmlType }
          label="Min value (Optional)"
          placeholder=""
          validationState={ this.state.minValidationState }
          value={ this.state.minHtml }
          onChange={e => this.minChanged(e.target.value, type)}
        />
      </Col>
      <Col xs={6}>
        <FieldGroup
          id="max"
          type={ htmlType }
          label="Max value (Optional)"
          placeholder=""
          validationState={ this.state.maxValidationState }
          value={ this.state.maxHtml }
          onChange={e => this.maxChanged(e.target.value, type)}
        />
      </Col>
    </div>
  }

  getOptionItem = index => (
    <Col xs={6} xsOffset={3} key={index}>
      <table>
        <tbody>
        <tr>
          <td>
            <FieldGroup
              id={ `option${index}` }
              type="text"
              label={ `Option ${index + 1}` }
              placeholder="Ex: Male or Female"
              validationState={ this.state.optionsValidationState[index] }
              value={ this.state.options[index] }
              onChange={e => this.optionChanged(e.target.value, index)}
              autoFocus={ index > 0 }
            />
          </td>
          <td>
            { index > 0 &&
            <span>&nbsp; &nbsp;
              <span className="red cursor-pointer" 
                onClick={ () => this.removeOption(index) }>
                <FontAwesome name="remove" title='Remove' />
              </span>
            </span>
            }
          </td>
        </tr>
        </tbody>
      </table>
    </Col>
  )

  renderOptionFields = () => {

    const options = this.state.options.map((_, index) => this.getOptionItem(index))

    return <div>
      { options }
      <Col xs={6} xsOffset={4}>
        <Button onClick={ this.addNewOption } bsStyle="default">
          <FontAwesome name="plus" /> New option
        </Button>
        <br/><br/>
      </Col>
    </div>
  }

  render() {
    const { show } = this.props
    const tab = ['Yes', 'No']
    return (
      <Modal show={ show } onHide={ this.closeModal }>

        <Modal.Header closeButton>
          <Modal.Title>Add a new customer field</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row>
            <Col xs={6}>
              <FormGroup controlId='type' validationState={ this.state.typeValidationState }>
                <ControlLabel>Type</ControlLabel>
                <FormControl componentClass="select" placeholder="select"
                  value={ this.state.type }
                  onChange={e => this.typeChanged(e.target.value)}>
                  <option value=""></option>
                  { customerFieldType.map((item) => <option key={ item } value={ item }>{ item }</option>) }
                </FormControl>
              </FormGroup>
            </Col>

            <Col xs={3}>
              <FormGroup controlId='mandatory' validationState='success'>
                <ControlLabel>Mandatory</ControlLabel>
                <ButtonToolbar>
                  <ToggleButtonGroup
                    onChange={ this.mandatoryChanged }
                    type="radio"
                    name="mandatory"
                    defaultValue={ this.state.mandatory ? 'Yes' : 'No' }>
                    { tab.map((value, index) => (
                      <ToggleButton key={ index } value={ value }>{ value }</ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </ButtonToolbar>
              </FormGroup>
            </Col>

            <Col xs={3}>
              <FormGroup controlId='unique' validationState='success'>
                <ControlLabel>Is unique</ControlLabel>
                <ButtonToolbar>
                  <ToggleButtonGroup
                    onChange={ this.uniqueChanged }
                    type="radio"
                    name="unique"
                    defaultValue={ this.state.unique ? 'Yes' : 'No' }>
                    { tab.map((value, index) => (
                      <ToggleButton key={ index } value={ value }>{ value }</ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </ButtonToolbar>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col xs={6}>
              <FieldGroup
                id="label"
                type="text"
                label="Label"
                placeholder="Ex: First name"
                validationState={ this.state.labelValidationState }
                value={ this.state.label }
                onChange={e => this.labelChanged(e.target.value)}
                maxLength={32}
                autoFocus
              />
            </Col>
            <Col xs={6}>
              <FieldGroup
                id="placeHolder"
                type="text"
                label="PlaceHolder"
                placeholder="Ex: Enter first name"
                validationState={ this.state.placeHolderValidationState }
                value={ this.state.placeHolder }
                onChange={e => this.placeHolderChanged(e.target.value)}
                maxLength={255}
              />
            </Col>
          </Row>

          <Row>
            { this.renderAdditionalFields() }
          </Row>

          <ProcessesStatus error={ this.state.error } />

        </Modal.Body>

        <Modal.Footer>
          <Button onClick={ this.closeModal }>Close</Button>
          <Button onClick={ this.saveField } bsStyle="primary">Add</Button>
        </Modal.Footer>

      </Modal>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/Settings/Customers/AddCustomerFieldModal.js